import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, FileSearch, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Full-page overlay shown while the contract-checker `/new` page is
 * running its AI analysis. Modelled on the template-generation
 * `LoadingScreen` — animated icon, faked progress bar tied to
 * synthetic stages, and a "Did you know?" tips carousel — but
 * lighter: no Turnstile retry, no real per-stage SSE events (the
 * actual analysis is a single ~20–40s call).
 */

interface StageDef {
  titleKey: string;
  titleFallback: string;
  /** Cumulative percent at the END of this stage (where it hands off). */
  end: number;
  /** Wall-clock seconds the bar spends crawling THROUGH this stage. */
  duration: number;
}

const STAGES: StageDef[] = [
  {
    titleKey: 'ORGCONTRACTCHECKER_ANALYZING_STAGE_READ',
    titleFallback: 'Reading the contract',
    end: 15,
    duration: 3,
  },
  {
    titleKey: 'ORGCONTRACTCHECKER_ANALYZING_STAGE_CLAUSES',
    titleFallback: 'Identifying material clauses',
    end: 45,
    duration: 8,
  },
  {
    titleKey: 'ORGCONTRACTCHECKER_ANALYZING_STAGE_RISK',
    titleFallback: 'Assessing risk from your perspective',
    end: 75,
    duration: 10,
  },
  {
    titleKey: 'ORGCONTRACTCHECKER_ANALYZING_STAGE_DRAFT',
    titleFallback: 'Drafting counter-language',
    end: 95,
    duration: 12,
  },
];

const TIP_KEYS: ReadonlyArray<{ key: string; fallback: string }> = [
  {
    key: 'ORGCONTRACTCHECKER_TIP_1',
    fallback:
      'Counter-clauses are starting points — you can edit any proposed change before downloading the review.',
  },
  {
    key: 'ORGCONTRACTCHECKER_TIP_2',
    fallback:
      'Risk level reflects your role: a clause that protects the provider is often high-risk for the client.',
  },
  {
    key: 'ORGCONTRACTCHECKER_TIP_3',
    fallback:
      'High-risk findings appear first. Focus on those when prepping for a negotiation call.',
  },
  {
    key: 'ORGCONTRACTCHECKER_TIP_4',
    fallback:
      'The downloaded PDF reflects any edits you make to proposed changes or counter-clauses.',
  },
];

export function LoadingOverlay() {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);

  // Drive the fake progress bar. We advance through the four stages
  // in real time, then crawl from 95→99 indefinitely while we wait
  // for the actual AI response. When the real call returns the page
  // unmounts this component, so we never reach 100% from here.
  useEffect(() => {
    const startedAt = Date.now();
    const interval = setInterval(() => {
      const elapsedSec = (Date.now() - startedAt) / 1000;
      let cumulative = 0;
      let cap = 99;
      for (const stage of STAGES) {
        const stageElapsed = elapsedSec - cumulative;
        if (stageElapsed < stage.duration) {
          const prevEnd = STAGES.indexOf(stage) === 0 ? 0 : STAGES[STAGES.indexOf(stage) - 1].end;
          const ratio = Math.max(0, Math.min(1, stageElapsed / stage.duration));
          cap = prevEnd + (stage.end - prevEnd) * ratio;
          break;
        }
        cumulative += stage.duration;
        cap = stage.end;
      }
      if (elapsedSec > STAGES.reduce((acc, s) => acc + s.duration, 0)) {
        // Once we've burned through all four stages, creep slowly
        // toward 99 — the real call should land before this matters.
        cap = Math.min(
          99,
          95 + (elapsedSec - STAGES.reduce((acc, s) => acc + s.duration, 0)) * 0.2
        );
      }
      setProgress(cap);
    }, 200);
    return () => clearInterval(interval);
  }, []);

  // Rotate tips every 5 seconds.
  useEffect(() => {
    const interval = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % TIP_KEYS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Which stage is "active" right now? The first one whose `end` is
  // greater than current progress.
  const activeStageIndex = Math.min(
    STAGES.findIndex((s) => s.end > progress),
    STAGES.length - 1
  );
  const safeActiveIndex = activeStageIndex === -1 ? STAGES.length - 1 : activeStageIndex;
  const currentStage = STAGES[safeActiveIndex];

  return (
    <div className="relative flex min-h-[80dvh] items-center justify-center overflow-hidden px-3 sm:px-4 py-6">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,hsl(var(--brand-primary)/0.12),transparent_65%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_bottom,hsl(var(--brand-primary)/0.06),transparent_60%)]" />

      <div className="relative z-10 w-full max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="mb-6 sm:mb-10 text-center space-y-3 sm:space-y-5"
        >
          <motion.div
            className="mx-auto flex h-16 w-16 sm:h-24 sm:w-24 items-center justify-center rounded-2xl sm:rounded-3xl border-2 border-selise-blue/20 bg-linear-to-br from-selise-blue/10 to-selise-blue/5 shadow-lg backdrop-blur-sm"
            animate={{
              scale: [1, 1.05, 1],
              boxShadow: [
                '0 10px 25px -5px rgba(0, 89, 187, 0.10)',
                '0 15px 35px -5px rgba(0, 89, 187, 0.20)',
                '0 10px 25px -5px rgba(0, 89, 187, 0.10)',
              ],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="animate-pulse">
              <FileSearch className="h-8 w-8 sm:h-12 sm:w-12 text-selise-blue" />
            </div>
          </motion.div>

          <h2 className="text-2xl sm:text-4xl font-semibold text-[hsl(var(--fg))] font-heading tracking-tight">
            {t('ORGCONTRACTCHECKER_ANALYZING_TITLE', {
              defaultValue: 'Analyzing your contract…',
            })}
          </h2>
          <p className="text-sm sm:text-base text-globe-grey leading-relaxed max-w-md mx-auto">
            {t('ORGCONTRACTCHECKER_ANALYZING_DESC', {
              defaultValue:
                "This usually takes 20–40 seconds. We'll save the result to your library.",
            })}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="rounded-2xl sm:rounded-3xl border border-border bg-card p-4 sm:p-8 shadow-[0_4px_20px_rgba(0,31,53,0.08)]"
        >
          <div className="mb-3 flex items-center justify-between gap-2 text-xs sm:text-sm font-medium">
            <span className="text-[hsl(var(--fg))]">
              {t('ORGCONTRACTCHECKER_ANALYZING_PROGRESS', {
                defaultValue: '{{percent}}% complete',
                percent: Math.round(progress),
              })}
            </span>
            <span className="text-globe-grey">
              {t('ORGCONTRACTCHECKER_ANALYZING_STEPXOFY', {
                defaultValue: 'Step {{current}} of {{total}}',
                current: safeActiveIndex + 1,
                total: STAGES.length,
              })}
            </span>
          </div>

          <div className="relative h-3 sm:h-4 overflow-hidden rounded-full border border-selise-blue/25 bg-selise-blue/10 shadow-inner">
            <motion.div
              className="absolute inset-0 bg-linear-to-r from-transparent via-selise-blue/20 to-transparent"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="absolute left-0 top-0 h-full rounded-full bg-linear-to-r from-selise-blue via-selise-blue/90 to-selise-blue shadow-lg"
              style={{ width: `${progress}%` }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            />
            <motion.div
              className="absolute top-1/2 h-4 w-4 sm:h-5 sm:w-5 -translate-y-1/2 rounded-full border-2 border-white bg-selise-blue shadow-[0_0_0_3px_rgba(255,255,255,0.5),0_4px_12px_rgba(0,89,187,0.5)]"
              style={{ left: `${progress}%`, marginLeft: '-10px' }}
              animate={{
                scale: [1, 1.15, 1],
              }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={safeActiveIndex}
              initial={{ opacity: 0, y: 8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="mt-4 sm:mt-6 rounded-xl sm:rounded-2xl border border-border bg-linear-to-br from-card to-selise-blue/2 p-4 sm:p-6"
            >
              <div className="flex items-start gap-3 sm:gap-5">
                <div className="flex h-10 w-10 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-lg sm:rounded-xl border-2 border-selise-blue/20 bg-linear-to-br from-selise-blue/10 to-selise-blue/5">
                  <div className="animate-pulse">
                    <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-selise-blue" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-[hsl(var(--fg))] font-heading">
                    {t(currentStage.titleKey, { defaultValue: currentStage.titleFallback })}
                  </h3>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-5 sm:mt-6 space-y-2 sm:space-y-3">
            {STAGES.map((stage, index) => {
              const isCompleted = index < safeActiveIndex;
              const isCurrent = index === safeActiveIndex;
              return (
                <motion.div
                  key={stage.titleKey}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-center gap-2.5 sm:gap-3 text-xs sm:text-sm transition-all duration-300 ${
                    isCompleted
                      ? 'text-selise-blue'
                      : isCurrent
                        ? 'text-[hsl(var(--fg))] font-medium'
                        : 'text-globe-grey'
                  }`}
                >
                  {isCompleted ? (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      className="flex h-5 w-5 items-center justify-center rounded-full bg-selise-blue text-white"
                    >
                      <Check className="h-3 w-3" />
                    </motion.div>
                  ) : isCurrent ? (
                    <motion.div
                      animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                      className="h-5 w-5 rounded-full border-2 border-selise-blue bg-selise-blue/10"
                    />
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2 border-border bg-border/30" />
                  )}
                  <span className={isCurrent ? 'font-medium' : ''}>
                    {t(stage.titleKey, { defaultValue: stage.titleFallback })}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-4 sm:mt-6 rounded-xl border border-border bg-card p-3 sm:p-4"
        >
          <div className="flex items-start gap-2.5 sm:gap-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-selise-blue/10 flex items-center justify-center shrink-0">
              <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-selise-blue" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-semibold text-selise-blue uppercase tracking-wide mb-0.5 sm:mb-1">
                {t('ORGCONTRACTCHECKER_DIDYOUKNOW', { defaultValue: 'Did you know?' })}
              </p>
              <AnimatePresence mode="wait">
                <motion.p
                  key={tipIndex}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.3 }}
                  className="text-xs sm:text-sm text-globe-grey leading-relaxed"
                >
                  {t(TIP_KEYS[tipIndex].key, { defaultValue: TIP_KEYS[tipIndex].fallback })}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>
          <div className="flex items-center justify-center gap-1.5 mt-2 sm:mt-3">
            {TIP_KEYS.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full transition-all ${
                  index === tipIndex ? 'w-4 bg-selise-blue' : 'w-1.5 bg-border'
                }`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default LoadingOverlay;
